import { Directive, HostListener, Input } from '@angular/core';
import { DxSelectBoxComponent, DxTagBoxComponent } from 'devextreme-angular';
import { Utils } from '@core/helpers/Utils';

@Directive({ selector: '[configSearch]' })
export class M4UConfigSearchDirective {

    //Set default options
    constructor(private c: DxSelectBoxComponent) {
        //Enables typing in
        c.searchEnabled = true;

        //Set mode. This is what supported on the server
        c.searchMode = "contains";

        this.SetItemTemplate();
    }

    searchText: string = ''

    //Set some properties related to server search mode
    _DataMode: string;
    get DataMode(): string { return this._DataMode; }
    @Input() set DataMode(value: string) {
        this._DataMode = value;

        //If local, no need for minimum
        this.c.minSearchLength = (this._DataMode == 'server' ? 2 : 0);

        //Opening causes a full load. If in client mode, we don't care
        this.c.openOnFieldClick = (this._DataMode == 'server' ? false : true);
    }

    _ShowExtra: boolean;
    get ShowExtra(): boolean { return this._ShowExtra; }
    @Input() set ShowExtra(value: boolean) {
        this._ShowExtra = value;
        this.SetDisplayExpression();
        this.SetItemTemplate();
    }

    //Set template that highlights the typed in item, shows the value, and enables search by value
    _ShowValue: boolean;
    get ShowValue(): boolean { return this._ShowValue; }
    @Input() set ShowValue(value: boolean) {
        this._ShowValue = value;
        this.SetDisplayExpression();
        this.SetItemTemplate()
    }

    //Set the template
    SetItemTemplate() {
        this.c.itemTemplate = this.PrepareItemTemplate.bind(this);
    }

    //Have to expose this one as well to allow overriding it, since the code itself overrides this field on the base control
    _DisplayExpr: string;
    get DisplayExpr(): string { return this._DisplayExpr; }
    @Input() set DisplayExpr(value: string) {
        this._DisplayExpr = value;
        this.SetDisplayExpression();
    }

    @HostListener('onInput') onInput() {
        this.searchText = String(this.c.instance.option('text'))
    }

    @HostListener('onSelectionChanged') onSelectionChanged() {
        this.searchText = ''
    }

    //Set correct display and search strings
    SetDisplayExpression() {

        //Set display expression to the one set manually, or build an automatic one depending on the Value/Text fields
        if (this.DisplayExpr != undefined) {
            this.c.displayExpr = this.DisplayExpr;
        } else {
            if (this._ShowValue || (this._ShowExtra && this.ExtraField)) {
                this.c.displayExpr = (item) => (item != null
                    ? `${Utils.NZ(item[this._ShowExtra ? this.ExtraField : this.ValueField])} - ${Utils.NZ(item[this.TextField])}`
                    : null);
            }
            else {
                this.c.displayExpr = this.TextField;
            }
        }

        const arr = [];
        arr.push(this.TextField);
        if (this.ShowValue) arr.push(this.ValueField);
        if (this.ShowExtra) arr.push(this.ExtraField);
        this.c.searchExpr = arr;
    }

    //Set that field that is being used as a text, to allow customer datasources
    _TextField: string = "Text";
    get TextField(): string { return this._TextField; }
    @Input() set TextField(value: string) {
        if (this._TextField != value) {
            this._TextField = value;
            this.SetDisplayExpression();
        }
    }

    //Set that field that is being used as a value, to allow customer datasources
    _ValueField: string = "Value";
    get ValueField(): string { return this._ValueField; }
    @Input() set ValueField(value: string) {
        if (this._ValueField != value) {
            this._ValueField = value;
            value && (this.c.valueExpr = value);
            this.SetDisplayExpression();
        }
    }

    //Set that field that is being used as a extra 
    _ExtraField: string = "Extra";
    get ExtraField(): string { return this._ExtraField; }
    @Input() set ExtraField(extra: string) {
        if (this._ExtraField != extra) {
            this._ExtraField = extra;
            this.SetDisplayExpression();
        }
    }

    private GetValueFromPath(obj: any, path: string): string {
        const value = path.split('.').reduce((acc, val) => acc && acc[val], obj);
        return typeof value === 'number' ? value.toString() : value;
    }

    //Prepare the template
    private PrepareItemTemplate(itemData, itemIndex, itemElement): string {
        var sEnteredString: string = this.searchText;

        //Get extra
        var sExtraPart: string = '';
        if (this.ShowExtra) sExtraPart = this.GetHighlightedString(sEnteredString, this.GetValueFromPath(itemData, this.ExtraField), "extra");

        //Return either both parts, or just one
        if (this._ShowValue) {
            var sValuePart: string = this.GetHighlightedString(sEnteredString, `${this.GetValueFromPath(itemData, this.ValueField)}`, "value");
            var sTextPart: string = this.GetHighlightedString(sEnteredString, this.GetValueFromPath(itemData, this.TextField), "text");
            return `${sValuePart}${sTextPart}` + (this.ShowExtra ? sExtraPart : '');
        }
        else {
            var sTextPart: string = this.GetHighlightedString(sEnteredString, this.GetValueFromPath(itemData, this.TextField), "textnovalue");
            return sTextPart + (this.ShowExtra ? sExtraPart : '');;
        }
    }

    //Return a highlighten string for a line
    private GetHighlightedString(sEnteredString: string, sItemString: string, sClass: string): string {
        var sValue: string;
        if (!Utils.IsEmpty(sEnteredString) && !Utils.IsEmpty(sItemString)) {
            var iPos = sItemString.toLowerCase().indexOf(sEnteredString.toLowerCase());
            if (iPos >= 0) {
                //Put the text we found inside a span
                var iLen = sEnteredString.length;
                sValue = `${sItemString.slice(0, iPos)}<span class='m4u-search-item-highlight'>${sItemString.slice(iPos, iPos + iLen)}</span>${sItemString.slice(iPos + iLen)}`;
            }
            else {
                //Not found
                sValue = Utils.NZ(sItemString);
            }
        }
        else {
            //Same as not found
            sValue = Utils.NZ(sItemString);
        }

        //Return div for this line
        return `<div class='m4u-item-${sClass}'>${sValue}</div>`;
    }
}
